/**
 * Générateur — Simplifier l'écriture de sommes comportant des parenthèses.
 * Attendu ID : 920 | Niveau : 5ème | Domaine : Nombres relatifs
 *
 * Libellé officiel (spécification 920.json) : « Simplifier l'écriture de sommes
 * comportant des parenthèses. »
 *
 * Plan de cartes (version conforme à la spécification, campagne 5e 2026-09-22) :
 *
 *   Carte « simplifier » — Écrire sans parenthèses (niveaux 1 à 3)
 *     La tâche s'ARRÊTE à l'expression simplifiée : aucun calcul n'est demandé.
 *     texte_trou_saisie : un trou par jonction de signes (l'échafaudage) ;
 *     texte : l'expression simplifiée en saisie libre, comparée sur la FORME
 *     (comparaison d'expression, liste d'écritures admises fixée d'avance) —
 *     le résultat du calcul n'y figure pas, et la consigne dit pourquoi il
 *     serait refusé ;
 *     qcm_unique : quatre écritures qui ne diffèrent que par leurs SIGNES,
 *     chacune correcte pour le tirage qui lui correspond ;
 *     tableau_vf : cinq gabarits à prédicat variable.
 *
 *   Carte « calcul » — Simplifier puis calculer (niveaux 4 et 5)
 *     texte_trou_saisie : les jonctions de signes, puis le résultat — deux
 *     temps notés séparément, le second n'apparaissant que sur cette carte.
 *
 *   Carte « englobante » — La parenthèse qui englobe (niveau 6)
 *     Une parenthèse englobant une somme de termes parenthésés, précédée d'un
 *     signe : un seul niveau d'imbrication. texte_trou_saisie et qcm_unique.
 *
 * Spécification (920.json) :
 *   Bornes appliquées : operation_de_l_expression = addition exclusivement,
 *     aucun produit (`origine: prescrit`) ; presence_du_calcul_final = réglage,
 *     désactivé aux niveaux 1 à 3 (carte `simplifier`), activé aux niveaux 4 à
 *     6 (cartes `calcul` et `englobante`) ; nombre_de_termes 2..4 ;
 *     nombre_de_paires_de_parentheses 1..4, au moins un terme parenthésé ;
 *     niveau_d_imbrication 0..1, l'imbrication n'étant produite que par la
 *     carte `englobante` ; termes_de_la_somme -20..20, aucun terme nul ;
 *     nombre_de_decimales_de_chaque_terme 0..2 ;
 *     combinaisons_de_signes : les quatre jonctions + (+b), + (-b), - (+b),
 *     - (-b), tirées indépendamment sur chaque jonction.
 *   Exclusions : aucun produit ; aucun terme nul ; aucune somme sans terme
 *     parenthésé ; aucune imbrication au-delà d'un niveau ; le résultat du
 *     calcul n'est jamais une réponse admise quand seule la simplification est
 *     demandée, et la consigne explique ce refus ; aucune parenthèse résiduelle
 *     dans l'expression attendue.
 *   Erreurs visées → mots-clés (_meta.params.distracteurs[].code,
 *   _meta.params.lignes[].code, erreurVisee) :
 *     « Supprimer la parenthèse d'un terme négatif précédé d'un + sans
 *       transformer + (-b) en - b » → plus_moins_non_transforme
 *         (simplifier_qcm_unique, simplifier_vf, simplifier_texte_trou_saisie)
 *     « Changer le signe de tous les termes d'une parenthèse précédée d'un + »
 *         → signes_changes_apres_plus (simplifier_qcm_unique, englobante_qcm_unique)
 *     « Calculer au lieu de réécrire » → calcul_au_lieu_de_reecriture
 *         (simplifier_texte : le résultat n'est pas une écriture admise, et la
 *         consigne le dit ; simplifier_vf)
 *     « Écrire deux signes côte à côte » → deux_signes_cote_a_cote (simplifier_vf)
 *     « Perdre le signe du premier terme quand il perd sa parenthèse »
 *         → signe_du_premier_perdu (simplifier_qcm_unique, englobante_qcm_unique)
 *     « Croire que la simplification change la valeur de la somme »
 *         → valeur_changee_par_simplification (simplifier_vf, calcul)
 *     « Ajouter des parenthèses là où il n'y en avait pas »
 *         → parentheses_ajoutees (simplifier_vf, simplifier_texte)
 *   Graduation → cartes :
 *     niveau 1 (deux termes tous deux parenthésés, entiers) et niveau 2 (deux
 *       termes, les quatre combinaisons) → carte simplifier, tirages à deux
 *       termes ; niveau 3 (trois ou quatre termes, entiers) → carte simplifier ;
 *     niveau 4 (une décimale) et niveau 5 (deux décimales) → carte calcul ;
 *     niveau 6 (parenthèse englobante) → carte englobante.
 *   Non tranché : l'expression simplifiée n'est saisie en toutes lettres que
 *     dans `simplifier_texte` ; ailleurs elle est complétée signe par signe,
 *     faute de champ libre dans un texte à trous ; « les quatre combinaisons
 *     côte à côte dans la même série » n'est atteignable qu'en série, une
 *     expression n'ayant qu'une à trois jonctions.
 *
 * Arithmétique en CENTIÈMES entiers. Signe « - » = tiret clavier partout.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 920;

const L_SIMPLIFIER = 'Écrire sans parenthèses (écriture simplifiée)';
const L_CALCUL = 'Simplifier puis calculer';
const L_ENGLOBANTE = 'La parenthèse qui englobe';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Centièmes entiers → écriture française. cent(0) = « 0 ». */
const cent = (c) => {
    const s = c < 0 ? '-' : '';
    const a = Math.abs(c);
    const ent = Math.floor(a / 100);
    const r = a % 100;
    if (r === 0) return `${s}${ent}`;
    if (r % 10 === 0) return `${s}${ent},${r / 10}`;
    return `${s}${ent},${String(r).padStart(2, '0')}`;
};
const signe = (c) => (c === 0 ? '0' : c < 0 ? cent(c) : `+${cent(c)}`);
const paren = (c) => `(${signe(c)})`;

const REFUS_EXPLIQUE = 'On demande l\'écriture, pas la valeur : si tu donnes le résultat du calcul, '
    + 'la réponse est refusée.';

/** Un terme non nul, |v| ≤ 20, avec exactement `dec` décimales. */
const tirerTerme = (dec) => {
    if (dec === 0) return randInt(1, 20) * 100;
    if (dec === 1) {
        let k = randInt(1, 199);
        if (k % 10 === 0) k += 1;
        return k * 10;
    }
    let k = randInt(1, 1999);
    if (k % 10 === 0) k += 1;
    return k;
};
const tirerRelatif = (dec) => randomChoice([1, -1]) * tirerTerme(dec);

/**
 * Une expression : un premier terme t0 et une à trois JONCTIONS { op, v } où
 * `op` est le signe de l'opération et `v` le nombre relatif écrit entre
 * parenthèses. La contribution d'une jonction vaut op === '+' ? v : -v.
 * Les quatre combinaisons de signes sont tirées indépendamment.
 */
const tirerExpression = (nTermes, dec, toutParenthese) => {
    const t0 = tirerRelatif(randomChoice(dec));
    const jonctions = [];
    for (let i = 1; i < nTermes; i++) {
        jonctions.push({
            op: randomChoice(['+', '-']),
            v: tirerRelatif(randomChoice(dec)),
            // Au moins un terme parenthésé : un terme n'est écrit nu que s'il
            // est positif, et jamais pour le premier des termes de droite.
            nu: i > 1 && randomChoice([true, false]),
        });
    }
    return { t0, jonctions, toutParenthese };
};

const contributions = (e) => e.jonctions.map(j => (j.op === '+' ? j.v : -j.v));

/** L'expression telle qu'elle est écrite, avec ses parenthèses. */
const ecrire = (e) => {
    const tete = e.toutParenthese ? paren(e.t0) : cent(e.t0);
    return [tete, ...e.jonctions.map(j => `${j.op} ${j.nu && j.v > 0 ? cent(j.v) : paren(j.v)}`)].join(' ');
};

/** L'écriture simplifiée : aucune parenthèse, un signe par jonction. */
const simplifiee = (e, signes) => {
    const c = contributions(e);
    const s = signes || [e.t0 < 0, ...c.map(v => v < 0)];
    return [`${s[0] ? '-' : ''}${cent(Math.abs(e.t0))}`,
        ...c.map((v, i) => `${s[i + 1] ? '-' : '+'} ${cent(Math.abs(v))}`)].join(' ');
};

/** Les signes de l'écriture simplifiée : [signe de t0, puis un par jonction]. */
const signesDe = (e) => [e.t0 < 0, ...contributions(e).map(v => v < 0)];

const total = (e) => e.t0 + contributions(e).reduce((a, b) => a + b, 0);

/** Tire nb vérités avec au moins une vraie et une fausse. */
const tirerVerites = (nb) => {
    const v = [];
    for (let i = 0; i < nb; i++) v.push(randomChoice([true, false]));
    if (v.every(x => x)) v[randInt(0, nb - 1)] = false;
    else if (v.every(x => !x)) v[randInt(0, nb - 1)] = true;
    return v;
};

// ═════════ Carte « simplifier » — l'écriture seule (niveaux 1 à 3) ═════════

const NIVEAUX_SIMPLIFIER = [
    { niveau: 1, n: 2, dec: [0], tout: true },
    { niveau: 2, n: 2, dec: [0], tout: false },
    { niveau: 3, n: 3, dec: [0], tout: false },
    { niveau: 3, n: 4, dec: [0], tout: false },
];

const CONSIGNE_SIGNES = 'Complète chaque signe de l\'écriture simplifiée (tape + ou -).';

const genSimplifierTrou = () => {
    const cfg = randomChoice(NIVEAUX_SIMPLIFIER);
    const e = tirerExpression(cfg.n, cfg.dec, cfg.tout);
    const c = contributions(e);
    const droite = [`${e.t0 < 0 ? '-' : ''}${cent(Math.abs(e.t0))}`,
        ...c.map(v => `{{${v < 0 ? '-' : '+'}}} ${cent(Math.abs(v))}`)].join(' ');
    const payload = makeTexteTrou(`${ecrire(e)} = ${droite}`,
        `<p><b>Écris cette somme sans parenthèses.</b> ${CONSIGNE_SIGNES} ${REFUS_EXPLIQUE}</p>`,
        { ...opts(L_SIMPLIFIER), mode: 'saisie' });
    payload._meta.params = {
        t0: e.t0, jonctions: e.jonctions, niveau: cfg.niveau,
        erreurVisee: 'plus_moins_non_transforme',
    };
    return payload;
};

const genSimplifierSaisie = () => {
    const cfg = randomChoice(NIVEAUX_SIMPLIFIER);
    const e = tirerExpression(cfg.n, cfg.dec, cfg.tout);
    const canonique = simplifiee(e);
    // Liste FIXÉE D'AVANCE des écritures admises : la forme canonique, et la
    // même avec un + explicite devant un premier terme positif. Le résultat du
    // calcul n'y figure jamais.
    const acceptees = e.t0 > 0 ? [canonique, `+${canonique}`] : [canonique];
    const payload = makeSaisie(canonique,
        `<p><b>Écris ${ecrire(e)} sans parenthèses.</b> N'effectue pas le calcul. ${REFUS_EXPLIQUE}</p>`,
        { ...opts(L_SIMPLIFIER), comparaison: 'expression', reponses_acceptees: acceptees });
    payload._meta.params = {
        t0: e.t0, jonctions: e.jonctions, niveau: cfg.niveau, acceptees,
        erreurVisee: 'calcul_au_lieu_de_reecriture',
    };
    return payload;
};

/**
 * Quatre écritures qui ne diffèrent que par leurs SIGNES : la bonne, celle où
 * une jonction « + (-b) » n'a pas été transformée, celle où tous les signes de
 * jonction sont inversés, et celle où le signe du premier terme a changé.
 * Toutes les quatre sont des motifs de signes, aucune n'est reconnaissable
 * autrement que par le calcul de la jonction.
 */
const genSimplifierQcm = () => {
    const construire = () => {
        // Au moins DEUX jonctions : avec une seule, « une jonction non
        // transformée » et « toutes les jonctions inversées » donneraient la
        // même proposition. La jonction non transformée est tirée au hasard,
        // sans quoi la bonne réponse porterait toujours au moins un signe -
        // et son motif serait reconnaissable sans calcul.
        const e = tirerExpression(randomChoice([3, 4]), [0], false);
        const i = randInt(0, e.jonctions.length - 1);
        const base = signesDe(e);
        const variantes = [
            { signes: base, code: null },
            {
                signes: base.map((s, k) => (k === i + 1 ? !s : s)),
                code: e.jonctions[i].op === '+' && e.jonctions[i].v < 0
                    ? 'plus_moins_non_transforme'
                    : 'signes_changes_apres_plus',
            },
            { signes: base.map((s, k) => (k === 0 ? s : !s)), code: 'signes_changes_apres_plus' },
            { signes: base.map((s, k) => (k === 0 ? !s : s)), code: e.t0 < 0 ? 'signe_du_premier_perdu' : 'signes_changes_apres_plus' },
        ];
        const textes = variantes.map(v => simplifiee(e, v.signes));
        return new Set(textes).size === 4 ? { e, variantes, textes } : null;
    };
    let t = construire();
    for (let essai = 0; essai < 80 && !t; essai++) t = construire();
    if (!t) { // repli déterministe, vérifié à la main
        const e = {
            t0: -300,
            jonctions: [{ op: '+', v: -500, nu: false }, { op: '-', v: 200, nu: false }],
            toutParenthese: false,
        };
        const base = signesDe(e);
        const variantes = [
            { signes: base, code: null },
            { signes: base.map((s, k) => (k === 1 ? !s : s)), code: 'plus_moins_non_transforme' },
            { signes: base.map((s, k) => (k === 0 ? s : !s)), code: 'signes_changes_apres_plus' },
            { signes: base.map((s, k) => (k === 0 ? !s : s)), code: 'signe_du_premier_perdu' },
        ];
        t = { e, variantes, textes: variantes.map(v => simplifiee(e, v.signes)) };
    }
    const payload = makeQcm(t.textes, t.textes[0],
        `<p><b>Quelle est l'écriture simplifiée de ${ecrire(t.e)} ?</b> ${REFUS_EXPLIQUE}</p>`,
        opts(L_SIMPLIFIER));
    payload._meta.params = {
        t0: t.e.t0, jonctions: t.e.jonctions, niveau: 3,
        distracteurs: t.variantes.slice(1).map((v, k) => ({ valeur: t.textes[k + 1], code: v.code })),
    };
    return payload;
};

/** Gabarits à prédicat variable. ctx : { e, p (positif), n (négatif) }. */
const GABARITS_SIMPLIFIER = [
    {
        id: 'calcul', code: 'calcul_au_lieu_de_reecriture',
        ligne: (v, c) => `Simplifier l'écriture de ${ecrire(c.e)}, c'est écrire `
            + `${v ? simplifiee(c.e) : cent(total(c.e))}.`,
    },
    {
        id: 'valeur', code: 'valeur_changee_par_simplification',
        ligne: v => (v
            ? 'Une somme et son écriture simplifiée ont la même valeur.'
            : 'Simplifier l\'écriture d\'une somme change la valeur de cette somme.'),
    },
    {
        id: 'deux_signes', code: 'deux_signes_cote_a_cote',
        ligne: (v, c) => `L'écriture ${cent(c.p)} ${v ? '-' : '+ -'}${v ? ' ' : ''}${cent(-c.n)} est correcte.`,
    },
    {
        id: 'parentheses', code: 'parentheses_ajoutees',
        ligne: v => (v
            ? 'Dans une écriture simplifiée, il ne reste aucune parenthèse.'
            : 'Dans une écriture simplifiée, on garde des parenthèses autour des nombres négatifs.'),
    },
    {
        id: 'jonction', code: 'plus_moins_non_transforme',
        ligne: (v, c) => `Dans une somme, « + ${paren(c.n)} » s'écrit « ${v ? '-' : '+'} ${cent(-c.n)} ».`,
    },
];

const genSimplifierVf = () => {
    const ctx = {
        e: tirerExpression(randomChoice([2, 3]), [0, 1], false),
        p: tirerTerme(randomChoice([0, 1])),
        n: -tirerTerme(randomChoice([0, 1])),
    };
    const gabarits = shuffle(GABARITS_SIMPLIFIER).slice(0, 4);
    const verites = tirerVerites(4);
    const lignes = gabarits.map((g, i) => ({
        template: g.id, code: g.code, vrai: verites[i], texte: g.ligne(verites[i], ctx),
    }));
    const payload = makeVraiFaux(
        lignes.map(l => l.texte),
        lignes.map(l => (l.vrai ? 0 : 1)),
        '<p><b>Pour chaque affirmation, coche Vrai ou Faux.</b></p>',
        opts(L_SIMPLIFIER));
    payload._meta.params = { t0: ctx.e.t0, jonctions: ctx.e.jonctions, niveau: 2, lignes };
    return payload;
};

// ═════════ Carte « calcul » — simplifier PUIS calculer (niveaux 4 et 5) ═════════

const genCalcul = () => {
    const niveau = randomChoice([4, 5]);
    const e = tirerExpression(randInt(3, 4), niveau === 4 ? [1, 0] : [2, 2], false);
    const c = contributions(e);
    const droite = [`${e.t0 < 0 ? '-' : ''}${cent(Math.abs(e.t0))}`,
        ...c.map(v => `{{${v < 0 ? '-' : '+'}}} ${cent(Math.abs(v))}`)].join(' ');
    const payload = makeTexteTrou(`${ecrire(e)} = ${droite} = {{${cent(total(e))}}}`,
        '<p><b>Écris cette somme sans parenthèses, puis calcule.</b> '
        + `${CONSIGNE_SIGNES} Le dernier champ attend le résultat. `
        + 'Chaque champ compte pour un point.</p>',
        { ...opts(L_CALCUL), mode: 'saisie' });
    payload._meta.params = {
        t0: e.t0, jonctions: e.jonctions, niveau,
        erreurVisee: 'valeur_changee_par_simplification',
    };
    return payload;
};

// ═════════ Carte « englobante » — un seul niveau d'imbrication (niveau 6) ═════════

/** `t0 op ((v1) + (v2))` : le signe devant la parenthèse englobante agit sur les deux termes. */
const tirerEnglobante = () => {
    const dec = randomChoice([[0], [1, 0], [2, 2]]);
    return {
        t0: tirerRelatif(randomChoice(dec)),
        op: randomChoice(['+', '-']),
        v1: tirerRelatif(randomChoice(dec)),
        v2: tirerRelatif(randomChoice(dec)),
    };
};

const ecrireEnglobante = (g) => `${cent(g.t0)} ${g.op} (${paren(g.v1)} + ${paren(g.v2)})`;
const contribsEnglobante = (g) => (g.op === '+' ? [g.v1, g.v2] : [-g.v1, -g.v2]);
const simplifieeEnglobante = (g, signes) => {
    const c = contribsEnglobante(g);
    const s = signes || [g.t0 < 0, ...c.map(v => v < 0)];
    return [`${s[0] ? '-' : ''}${cent(Math.abs(g.t0))}`,
        ...c.map((v, i) => `${s[i + 1] ? '-' : '+'} ${cent(Math.abs(v))}`)].join(' ');
};

const genEnglobanteTrou = () => {
    const g = tirerEnglobante();
    const c = contribsEnglobante(g);
    const droite = [`${g.t0 < 0 ? '-' : ''}${cent(Math.abs(g.t0))}`,
        ...c.map(v => `{{${v < 0 ? '-' : '+'}}} ${cent(Math.abs(v))}`)].join(' ');
    const somme = g.t0 + c[0] + c[1];
    const payload = makeTexteTrou(`${ecrireEnglobante(g)} = ${droite} = {{${cent(somme)}}}`,
        '<p><b>Écris cette somme sans parenthèses, puis calcule.</b> '
        + `${CONSIGNE_SIGNES} Le signe placé devant la grande parenthèse agit sur les deux termes qu'elle contient. `
        + 'Le dernier champ attend le résultat.</p>',
        { ...opts(L_ENGLOBANTE), mode: 'saisie' });
    payload._meta.params = { ...g, niveau: 6, erreurVisee: 'signes_changes_apres_plus' };
    return payload;
};

const genEnglobanteQcm = () => {
    const construire = () => {
        const g = tirerEnglobante();
        const base = [g.t0 < 0, ...contribsEnglobante(g).map(v => v < 0)];
        const variantes = [
            { signes: base, code: null },
            { signes: base.map((s, k) => (k === 0 ? s : !s)), code: 'signes_changes_apres_plus' },
            { signes: base.map((s, k) => (k === 1 ? !s : s)), code: 'plus_moins_non_transforme' },
            { signes: base.map((s, k) => (k === 0 ? !s : s)), code: g.t0 < 0 ? 'signe_du_premier_perdu' : 'signes_changes_apres_plus' },
        ];
        const textes = variantes.map(v => simplifieeEnglobante(g, v.signes));
        return new Set(textes).size === 4 ? { g, variantes, textes } : null;
    };
    let t = construire();
    for (let essai = 0; essai < 80 && !t; essai++) t = construire();
    if (!t) { // repli déterministe, vérifié à la main
        const g = { t0: 1200, op: '-', v1: -300, v2: 500 };
        const base = [false, true, true];
        const variantes = [
            { signes: base, code: null },
            { signes: [false, false, false], code: 'signes_changes_apres_plus' },
            { signes: [false, false, true], code: 'plus_moins_non_transforme' },
            { signes: [true, true, true], code: 'signe_du_premier_perdu' },
        ];
        t = { g, variantes, textes: variantes.map(v => simplifieeEnglobante(g, v.signes)) };
    }
    const payload = makeQcm(t.textes, t.textes[0],
        `<p><b>Quelle est l'écriture simplifiée de ${ecrireEnglobante(t.g)} ?</b> ${REFUS_EXPLIQUE}</p>`,
        opts(L_ENGLOBANTE));
    payload._meta.params = {
        ...t.g, niveau: 6,
        distracteurs: t.variantes.slice(1).map((v, k) => ({ valeur: t.textes[k + 1], code: v.code })),
    };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    simplifier_texte_trou_saisie: genSimplifierTrou,
    simplifier_texte: genSimplifierSaisie,
    simplifier_qcm_unique: genSimplifierQcm,
    simplifier_vf: genSimplifierVf,
    calcul_texte_trou_saisie: genCalcul,
    englobante_texte_trou_saisie: genEnglobanteTrou,
    englobante_qcm_unique: genEnglobanteQcm,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', texte: 'Saisie', qcm_unique: 'QCM', vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'simplifier', label: L_SIMPLIFIER, formatsList: buildFormats('simplifier') },
    { id: 'calcul', label: L_CALCUL, formatsList: buildFormats('calcul') },
    { id: 'englobante', label: L_ENGLOBANTE, formatsList: buildFormats('englobante') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
